import { useMemo, useState } from "react";
import { useEditorStore } from "../app/store";
import { Icon } from "../components/Icon";
import type { ControlDefinition } from "../domain/instrument";
import { cableEndpoints } from "../domain/patch";
import {
  getNormalledRouteStates,
  validateSignalPatch,
  type SignalWarning,
} from "../domain/signal-graph";
import { externalEndpoints, utilityNodes, workspaceDefinition } from "./workspace-ports";

type ControlFilter = "all" | "changed" | "continuous" | "discrete";

function discreteIndex(control: ControlDefinition, value: number) {
  return Math.round(value * Math.max(0, (control.options?.length ?? 1) - 1));
}

function discreteValue(control: ControlDefinition, index: number) {
  const lastIndex = Math.max(0, (control.options?.length ?? 1) - 1);
  return lastIndex ? index / lastIndex : 0;
}

function describeControl(control: ControlDefinition, value: number) {
  if (control.options) return control.options[discreteIndex(control, value)]?.label ?? "—";
  if (control.type === "button") return value >= 0.5 ? "Active" : "Inactive";
  return `${Math.round(value * 100)}%`;
}

function warningForCable(warnings: SignalWarning[], cableId: string) {
  return warnings.filter((warning) => warning.cableId === cableId);
}

export function PatchListView() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ControlFilter>("all");
  const {
    activeDocument,
    beginHistoryTransaction,
    canRedo,
    canUndo,
    cables,
    controlValues,
    deleteSelectedCable,
    endHistoryTransaction,
    redo,
    select,
    selectedId,
    setControlValue,
    setUtilityControlValue,
    setViewMode,
    undo,
  } = useEditorStore();

  const definition = useMemo(() => workspaceDefinition(activeDocument), [activeDocument]);
  const endpoints = useMemo(() => externalEndpoints(activeDocument), [activeDocument]);
  const utilities = useMemo(() => utilityNodes(activeDocument), [activeDocument]);
  const sectionNames = useMemo(
    () => new Map(definition.sections.map((section) => [section.id, section.label])),
    [definition],
  );
  const portNames = useMemo(
    () => new Map(definition.ports.map((port) => [port.id, port.label])),
    [definition],
  );
  const normalStates = useMemo(
    () => getNormalledRouteStates(definition, cables, controlValues),
    [cables, controlValues, definition],
  );
  const warnings = useMemo(() => validateSignalPatch(definition, cables), [cables, definition]);

  const controls = definition.controls.filter((control) => {
    const value = controlValues[control.id] ?? control.defaultValue;
    const changed = Math.abs(value - control.defaultValue) > 0.001;
    const haystack = `${control.label} ${sectionNames.get(control.sectionId) ?? ""}`.toLowerCase();
    if (query && !haystack.includes(query.trim().toLowerCase())) return false;
    if (filter === "changed" && !changed) return false;
    if (
      filter === "continuous" &&
      control.type !== "knob" &&
      control.type !== "slider" &&
      control.type !== "wheel"
    )
      return false;
    if (filter === "discrete" && !control.options && control.type !== "button") return false;
    return true;
  });
  const changedCount = definition.controls.filter(
    (control) =>
      Math.abs((controlValues[control.id] ?? control.defaultValue) - control.defaultValue) > 0.001,
  ).length;
  const brokenCount = normalStates.filter((state) => state.status === "broken").length;

  const describeEndpoint = (endpoint: (typeof cables)[number]["endpointA"]) =>
    endpoint.kind === "loose"
      ? (endpoint.label ?? "Loose end")
      : `${portNames.get(endpoint.portId) ?? endpoint.portId}${
          endpoint.plugStyle === "stackable" ? " [stackable]" : ""
        }`;

  return (
    <section className="patch-list-view" aria-label="Patch worksheet">
      <header className="list-view-header">
        <div>
          <span className="eyebrow">Document view</span>
          <h2>Patch worksheet</h2>
          <p>Every saved value, physical lead, internal normal, and routing check in one place.</p>
        </div>
        <dl>
          <div>
            <dt>Changed</dt>
            <dd>{changedCount}</dd>
          </div>
          <div>
            <dt>Cables</dt>
            <dd>{cables.length}</dd>
          </div>
          <div>
            <dt>Broken normals</dt>
            <dd>{brokenCount}</dd>
          </div>
          <div>
            <dt>Checks</dt>
            <dd>{warnings.length}</dd>
          </div>
          <div>
            <dt>Utilities</dt>
            <dd>{utilities.length}</dd>
          </div>
        </dl>
      </header>

      <div className="list-view-toolbar">
        <label className="list-search">
          <Icon name="search" size={15} />
          <input
            aria-label="Search controls"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search controls or sections"
            type="search"
            value={query}
          />
        </label>
        <label>
          <span>Show</span>
          <select
            aria-label="Filter controls"
            onChange={(event) => setFilter(event.target.value as ControlFilter)}
            value={filter}
          >
            <option value="all">All controls</option>
            <option value="changed">Changed only</option>
            <option value="continuous">Continuous controls</option>
            <option value="discrete">Switches and buttons</option>
          </select>
        </label>
        <span className="list-result-count">
          {controls.length} of {definition.controls.length} controls
        </span>
        <div className="list-history-controls" role="group" aria-label="Worksheet history">
          <button aria-label="Undo" disabled={!canUndo} onClick={undo} type="button">
            <Icon name="undo" size={15} />
            <span>Undo</span>
          </button>
          <button aria-label="Redo" disabled={!canRedo} onClick={redo} type="button">
            <Icon name="redo" size={15} />
            <span>Redo</span>
          </button>
        </div>
      </div>

      <div className="list-view-grid">
        <section className="worksheet-panel control-worksheet">
          <header>
            <div>
              <span>01</span>
              <h3>Control values</h3>
            </div>
            <small>Changes save immediately</small>
          </header>
          <div className="worksheet-table control-table" role="table" aria-label="Control values">
            <div className="worksheet-row table-heading" role="row">
              <span role="columnheader">Control</span>
              <span role="columnheader">Section</span>
              <span role="columnheader">Value</span>
              <span role="columnheader">Default</span>
            </div>
            {controls.map((control) => {
              const value = controlValues[control.id] ?? control.defaultValue;
              const changed = Math.abs(value - control.defaultValue) > 0.001;
              return (
                <div
                  className={`worksheet-row control-row${changed ? " is-changed" : ""}${selectedId === control.id ? " is-selected" : ""}`}
                  key={control.id}
                  onClick={() => select(control.id)}
                  role="row"
                >
                  <span className="control-name" role="cell">
                    <i />
                    {control.label}
                  </span>
                  <span className="control-section" role="cell">
                    {sectionNames.get(control.sectionId)}
                  </span>
                  <span className="control-editor" role="cell">
                    {control.options ? (
                      <select
                        aria-label={`${control.label} value`}
                        onChange={(event) =>
                          setControlValue(
                            control.id,
                            discreteValue(control, Number(event.target.value)),
                          )
                        }
                        onClick={(event) => event.stopPropagation()}
                        value={discreteIndex(control, value)}
                      >
                        {control.options.map((option, index) => (
                          <option key={option.id} value={index}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : control.type === "button" ? (
                      <button
                        aria-pressed={value >= 0.5}
                        className={value >= 0.5 ? "is-active" : ""}
                        onClick={(event) => {
                          event.stopPropagation();
                          setControlValue(control.id, value >= 0.5 ? 0 : 1);
                        }}
                        type="button"
                      >
                        {value >= 0.5 ? "Active" : "Inactive"}
                      </button>
                    ) : (
                      <label className="range-editor">
                        <input
                          aria-label={`${control.label} value`}
                          max="100"
                          min="0"
                          onChange={(event) =>
                            setControlValue(control.id, Number(event.target.value) / 100)
                          }
                          onPointerDown={() => beginHistoryTransaction(`Adjust ${control.label}`)}
                          onPointerUp={endHistoryTransaction}
                          onClick={(event) => event.stopPropagation()}
                          type="range"
                          value={Math.round(value * 100)}
                        />
                        <output>{Math.round(value * 100)}%</output>
                      </label>
                    )}
                  </span>
                  <span className="control-default" role="cell">
                    <span>{describeControl(control, control.defaultValue)}</span>
                    {changed && (
                      <button
                        aria-label={`Reset ${control.label}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setControlValue(control.id, control.defaultValue);
                        }}
                        type="button"
                      >
                        Reset
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
            {controls.length === 0 && (
              <p className="worksheet-empty">No controls match this view.</p>
            )}
          </div>
        </section>

        <div className="worksheet-side-column">
          <section className="worksheet-panel cable-worksheet">
            <header>
              <div>
                <span>02</span>
                <h3>Patch cables</h3>
              </div>
              <small>{cables.length} documented</small>
            </header>
            <ol className="cable-table">
              {cables.map((cable) => {
                const cableWarnings = warningForCable(warnings, cable.id);
                return (
                  <li className={selectedId === cable.id ? "is-selected" : ""} key={cable.id}>
                    <button
                      className="cable-list-select"
                      onClick={() => select(cable.id)}
                      type="button"
                    >
                      <b style={{ borderColor: cable.colour }}>{cable.number}</b>
                      <span>
                        <strong>{describeEndpoint(cable.endpointA)}</strong>
                        <i>→</i>
                        <strong>{describeEndpoint(cable.endpointB)}</strong>
                        {cable.branches?.map((branch, index) => (
                          <small key={index}>
                            Branch {index + 2}: {describeEndpoint(branch)}
                          </small>
                        ))}
                        {cable.prepared && <small>Prepared loose cable</small>}
                      </span>
                      {cableWarnings.length > 0 && (
                        <em>
                          {cableWarnings.length} check{cableWarnings.length === 1 ? "" : "s"}
                        </em>
                      )}
                    </button>
                    <button
                      aria-label={`Delete cable ${cable.number}`}
                      className="cable-list-delete"
                      onClick={() => {
                        select(cable.id);
                        deleteSelectedCable();
                      }}
                      type="button"
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>

          {endpoints.length > 0 && (
            <section className="worksheet-panel external-worksheet">
              <header>
                <div>
                  <span>03</span>
                  <h3>External equipment</h3>
                </div>
                <small>{endpoints.length} endpoints</small>
              </header>
              <ul>
                {endpoints.map((endpoint) => (
                  <li key={endpoint.id}>
                    <button onClick={() => select(endpoint.id)} type="button">
                      <span>
                        <strong>{endpoint.deviceName ?? endpoint.displayName}</strong>
                        <small>{endpoint.portName ?? endpoint.displayName}</small>
                      </span>
                      <em>
                        {endpoint.direction} · {endpoint.signalType} ·{" "}
                        {endpoint.connectorType ?? "other"}
                      </em>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {utilities.length > 0 && (
            <section className="worksheet-panel external-worksheet">
              <header>
                <div>
                  <span>04</span>
                  <h3>Patch utilities</h3>
                </div>
                <small>{utilities.length} placed</small>
              </header>
              <ul>
                {utilities.map((utility) => {
                  const attenuator = utility.utilityType.includes("attenuator");
                  const splitter = utility.utilityType === "splitter-cable";
                  const junction = utility.utilityType === "stackable-junction";
                  const physicalAssembly = splitter || junction;
                  const level = utility.controls.find((control) => control.id === "level");
                  const connected = physicalAssembly
                    ? utility.ports.filter((port) =>
                        cables.some((cable) =>
                          cableEndpoints(cable).some(
                            (endpoint) =>
                              endpoint.kind === "port" &&
                              endpoint.portId === `utility:${utility.id}:${port.id}`,
                          ),
                        ),
                      ).length
                    : 0;
                  return (
                    <li key={utility.id}>
                      <button onClick={() => select(utility.id)} type="button">
                        <span>
                          <strong>{utility.displayName}</strong>
                          <small>
                            {utility.manufacturer ??
                              (attenuator
                                ? "Directional attenuator"
                                : splitter
                                  ? "Passive splitter cable"
                                  : junction
                                    ? "Movable cable junction"
                                    : "Passive mult")}{" "}
                            {utility.model ?? ""}
                          </small>
                        </span>
                        <em>
                          {attenuator
                            ? "IN → OUT"
                            : physicalAssembly
                              ? `${connected}/${utility.ports.length} connected · ${
                                  utility.ports.length - connected
                                } unused`
                              : `${utility.ports.length} sockets · ${
                                  new Set(utility.ports.map((port) => port.groupId)).size
                                } groups`}
                        </em>
                      </button>
                      {attenuator && level && (
                        <label className="range-editor utility-range-editor">
                          <span>{level.label}</span>
                          <input
                            aria-label={`${utility.displayName} ${level.label} value`}
                            max="100"
                            min="0"
                            onChange={(event) =>
                              setUtilityControlValue(
                                utility.id,
                                level.id,
                                Number(event.target.value) / 100,
                              )
                            }
                            onPointerDown={() =>
                              beginHistoryTransaction(
                                `Adjust ${utility.displayName} ${level.label}`,
                              )
                            }
                            onPointerUp={endHistoryTransaction}
                            type="range"
                            value={Math.round(level.value * 100)}
                          />
                          <output>{Math.round(level.value * 100)}%</output>
                        </label>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section className="worksheet-panel normal-worksheet">
            <header>
              <div>
                <span>03</span>
                <h3>Internal normals</h3>
              </div>
              <small>
                {normalStates.length - brokenCount} active · {brokenCount} broken
              </small>
            </header>
            <ul>
              {normalStates.map((state) => (
                <li
                  className={`is-${state.status}${selectedId === state.route.id ? " is-selected" : ""}`}
                  key={state.route.id}
                >
                  <button onClick={() => select(state.route.id)} type="button">
                    <i />
                    <span>
                      <strong>{state.route.label}</strong>
                      <small>
                        {state.status === "broken"
                          ? `Overridden by ${state.brokenByCableIds.length} cable${state.brokenByCableIds.length === 1 ? "" : "s"}`
                          : "Factory path in use"}
                      </small>
                    </span>
                  </button>
                  <button
                    aria-label={`Trace ${state.route.label}`}
                    onClick={() => {
                      select(state.route.id);
                      setViewMode("flow");
                    }}
                    type="button"
                  >
                    Trace
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section className="worksheet-panel warning-worksheet">
            <header>
              <div>
                <span>04</span>
                <h3>Routing checks</h3>
              </div>
              <small>{warnings.length}</small>
            </header>
            {warnings.length ? (
              <ul>
                {warnings.map((warning) => (
                  <li className={`is-${warning.severity}`} key={warning.id}>
                    <i>{warning.severity === "warning" ? "!" : "i"}</i>
                    <span>{warning.message}</span>
                    {warning.cableId && (
                      <button onClick={() => select(warning.cableId!)} type="button">
                        Select
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="worksheet-empty">No routing checks for this patch.</p>
            )}
          </section>
        </div>
      </div>
    </section>
  );
}
